import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  Fade,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Stack,
  TextField,
  InputAdornment,
  TablePagination,
  Grid,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  OutlinedInput,
  TableSortLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import DateRangeIcon from '@mui/icons-material/DateRange';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useLocation } from 'react-router-dom';
import api from '../services/api';

const Jobs = () => {
  const { status } = useParams();
  const { getAccessToken } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [jobs, setJobs] = useState({
    complete: [],
    failed: [],
    in_progress: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState({
    open: false,
    jobUuid: null,
    fileName: null
  });
  
  // Search, filter, pagination, and sorting states
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  // Default to today for start date
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Start of today
  
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(null);
  
  // Check if we got a status from the dashboard link
  const initialStatus = location.state?.status || 'complete';
  const [selectedStatus, setSelectedStatus] = useState(initialStatus);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    totalPages: 0
  });
  const [sortConfig, setSortConfig] = useState({
    sortBy: 'file_name',
    sortOrder: 'asc'
  });

  // Handle search term change with debounce
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      setRefreshing(true);
      const token = await getAccessToken();
      
      // Build options object for API call
      const options = {
        search: debouncedSearchTerm,
        page: page + 1, // API uses 1-based indexing
        pageSize: rowsPerPage,
        sortBy: sortConfig.sortBy,
        sortOrder: sortConfig.sortOrder
      };
      
      // Add date filters if set
      if (startDate) {
        options.startDate = startDate.toISOString();
      }
      
      if (endDate) {
        options.endDate = endDate.toISOString();
      }
      
      // Handle status filtering
      let currentStatus = 'all';
      
      if (selectedStatus && selectedStatus !== 'all') {
        currentStatus = selectedStatus;
      }
      
      // Fetch jobs with filters
      const result = await api.getJobs(currentStatus, token, options);
      setJobs(result);
      
      // Set pagination info if available
      if (result.pagination) {
        setPagination({
          totalCount: result.pagination.totalCount,
          totalPages: result.pagination.totalPages
        });
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError('Failed to load job data. Please try again later.');
    } finally {
      setLoading(false);
      setTimeout(() => setRefreshing(false), 500);
    }
  };

  // Fetch jobs when filters change
  useEffect(() => {
    fetchJobs();
  }, [
    getAccessToken, 
    debouncedSearchTerm, 
    page, 
    rowsPerPage, 
    sortConfig,
    startDate,
    endDate,
    selectedStatus
  ]);
  
  // Store the current search parameters for polling
  const searchParams = React.useRef({
    search: debouncedSearchTerm,
    status: selectedStatus,
    startDate: startDate,
    endDate: endDate,
    page: page,
    rowsPerPage: rowsPerPage,
    sortConfig: sortConfig
  });
  
  // Update the reference when any parameter changes
  useEffect(() => {
    searchParams.current = {
      search: debouncedSearchTerm,
      status: selectedStatus,
      startDate: startDate,
      endDate: endDate,
      page: page,
      rowsPerPage: rowsPerPage,
      sortConfig: sortConfig
    };
  }, [debouncedSearchTerm, selectedStatus, startDate, endDate, page, rowsPerPage, sortConfig]);
  
  // Set up polling for refreshing job status
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        setRefreshing(true);
        const token = await getAccessToken();
        
        // Build options object using the stored search parameters
        const options = {
          search: searchParams.current.search,
          page: searchParams.current.page + 1,
          pageSize: searchParams.current.rowsPerPage,
          sortBy: searchParams.current.sortConfig.sortBy,
          sortOrder: searchParams.current.sortConfig.sortOrder
        };
        
        if (searchParams.current.startDate) {
          options.startDate = searchParams.current.startDate.toISOString();
        }
        
        if (searchParams.current.endDate) {
          options.endDate = searchParams.current.endDate.toISOString();
        }
        
        // Use the stored status
        let currentStatus = 'all';
        if (searchParams.current.status && searchParams.current.status !== 'all') {
          currentStatus = searchParams.current.status;
        }
        
        const result = await api.getJobs(currentStatus, token, options);
        setJobs(result);
        
        if (result.pagination) {
          setPagination({
            totalCount: result.pagination.totalCount,
            totalPages: result.pagination.totalPages
          });
        }
        
        setError(null);
      } catch (err) {
        console.error('Auto-refresh error:', err);
        // Don't show error for background refreshes
      } finally {
        setTimeout(() => setRefreshing(false), 500);
      }
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(interval);
  }, [getAccessToken]);

  const handleDownload = async (fileName) => {
    try {
      console.log('Downloading file:', fileName);
      setDownloadLoading(fileName);
      const token = await getAccessToken();
      await api.downloadSummary(fileName, token);
      setDownloadLoading(null);
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download summary. Please try again.');
      setDownloadLoading(null);
    }
  };
  
  // Handle showing delete confirmation dialog
  const handleShowDeleteConfirm = (jobUuid, fileName) => {
    setConfirmDeleteDialog({
      open: true,
      jobUuid,
      fileName
    });
  };
  
  // Handle closing delete confirmation dialog
  const handleCloseDeleteConfirm = () => {
    setConfirmDeleteDialog({
      open: false,
      jobUuid: null,
      fileName: null
    });
  };
  
  // Handle actual delete operation
  const handleDelete = async () => {
    const { jobUuid, fileName } = confirmDeleteDialog;
    
    try {
      setDeleteLoading(jobUuid);
      const token = await getAccessToken();
      await api.deleteSummary(jobUuid, token);
      
      // Close dialog
      handleCloseDeleteConfirm();
      
      // Refresh the job list
      fetchJobs();
      
      setDeleteLoading(null);
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete summary. Please try again.');
      setDeleteLoading(null);
      handleCloseDeleteConfirm();
    }
  };

  const getStatusColor = (status) => {
    if (status === 'complete') return '#4caf50';
    if (status === 'in_progress') return '#ff9800';
    return '#f44336';
  };
  
  // Handle search change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    // Reset to first page when searching
    setPage(0);
  };
  
  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };
  
  // Handle date filter changes
  const handleStartDateChange = (date) => {
    setStartDate(date);
    // Reset to first page when filter changes
    setPage(0);
  };
  
  const handleEndDateChange = (date) => {
    setEndDate(date);
    // Reset to first page when filter changes
    setPage(0);
  };
  
  // Handle status selection
  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
    // Reset to first page when filter changes
    setPage(0);
  };
  
  // Clear all filters
  const handleClearFilters = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setStartDate(today);
    setEndDate(null);
    setSelectedStatus('complete');
    setSearchTerm('');
    setPage(0);
  };
  
  // Handle sorting
  const handleSort = (column) => {
    setSortConfig(prevConfig => {
      // If clicking the same column, toggle direction
      if (prevConfig.sortBy === column) {
        return {
          ...prevConfig,
          sortOrder: prevConfig.sortOrder === 'asc' ? 'desc' : 'asc'
        };
      }
      
      // Set default direction based on column
      let defaultOrder = 'asc';
      if (column === 'request_start_time' || column === 'request_completion_time') {
        defaultOrder = 'desc';
      }
      
      return {
        sortBy: column,
        sortOrder: defaultOrder
      };
    });
    
    // Reset to first page when sorting changes
    setPage(0);
  };
  
  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatFileName = (fileName, jobData) => {
    // Extract name and date from the filename
    const parts = fileName?.split(' ') || [];
    if (parts.length >= 2) {
      const name = parts.slice(0, parts.length - 1).join(' ');
      const dateInfo = parts[parts.length - 1];
      
      return (
        <Box>
          <Typography variant="body1" fontWeight="medium">{name}</Typography>
          <Typography variant="caption" color="text.secondary">{dateInfo}</Typography>
          
          {/* Show submission date on mobile */}
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              display: { xs: 'block', md: 'none' },
              mt: 0.5
            }}
          >
            Submitted: {formatDate(jobData.start_time)}
            {jobData.completion_time && jobData.status === 'complete' && (
              <span> • Completed: {formatDate(jobData.completion_time)}</span>
            )}
          </Typography>
          
          {/* Show submitter on mobile */}
          {jobData.submitted_by && (
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ 
                display: { xs: 'block', sm: 'none' },
                mt: 0.5
              }}
            >
              By: {jobData.submitted_by}
            </Typography>
          )}
        </Box>
      );
    }
    return fileName;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    // Format as relative time for recent dates
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffInSeconds < 604800) { // Less than a week
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    } else {
      // Format date without seconds
      return date.toLocaleString(undefined, { 
        year: 'numeric', 
        month: 'numeric', 
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
      });
    }
  };

  // Card view for Mobile
  const renderJobCards = (status) => {
    // If 'all', combine all job statuses
    let jobList = [];
    if (status === 'all') {
      jobList = [
        ...(jobs.complete || []),
        ...(jobs.in_progress || []),
        ...(jobs.failed || [])
      ];
      
      // Re-sort the combined list based on current sort config
      jobList.sort((a, b) => {
        const aValue = a[sortConfig.sortBy === 'request_start_time' ? 'start_time' : 
                         sortConfig.sortBy === 'request_completion_time' ? 'completion_time' : 
                         sortConfig.sortBy];
        const bValue = b[sortConfig.sortBy === 'request_start_time' ? 'start_time' : 
                         sortConfig.sortBy === 'request_completion_time' ? 'completion_time' : 
                         sortConfig.sortBy];
        
        if (!aValue && !bValue) return 0;
        if (!aValue) return 1;
        if (!bValue) return -1;
        
        let comparison;
        if (sortConfig.sortBy.includes('time')) {
          // Safely handle date comparison
          try {
            const dateA = new Date(aValue);
            const dateB = new Date(bValue);
            // Check if dates are valid before comparing
            comparison = (dateA.toString() !== 'Invalid Date' && dateB.toString() !== 'Invalid Date') 
              ? dateA - dateB
              : String(aValue).localeCompare(String(bValue));
          } catch (e) {
            // Fallback to string comparison if date parsing fails
            comparison = String(aValue).localeCompare(String(bValue));
          }
        } else {
          comparison = String(aValue).localeCompare(String(bValue));
        }
          
        return sortConfig.sortOrder === 'asc' ? comparison : -comparison;
      });
    } else {
      jobList = jobs[status] || [];
    }
    
    if (jobList.length === 0) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
          <Typography color="text.secondary">No jobs found matching the current filters</Typography>
        </Box>
      );
    }
    
    return (
      <Stack spacing={2} sx={{ mt: 2 }}>
        {jobList.map((job, index) => (
          <Card 
            key={index} 
            sx={{ 
              borderLeft: `4px solid ${getStatusColor(job.status)}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
          >
            <CardContent sx={{ pb: 1, position: 'relative' }}>
              <Typography variant="subtitle1" fontWeight="medium" sx={{ wordBreak: 'break-word', pr: 9 }}>
                {job.file_name}
              </Typography>
              
              <Box sx={{ position: 'absolute', top: 12, right: 16, display: 'flex', gap: 1 }}>
                {job.status === 'complete' && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={{ 
                      minWidth: 0,
                      width: 40,
                      height: 40,
                      p: 0
                    }}
                    onClick={() => handleDownload(job.file_name)}
                    disabled={downloadLoading === job.file_name}
                  >
                    {downloadLoading === job.file_name ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <DownloadIcon />
                    )}
                  </Button>
                )}
                
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  sx={{ 
                    minWidth: 0,
                    width: 40,
                    height: 40,
                    p: 0
                  }}
                  onClick={() => handleShowDeleteConfirm(job.job_uuid, job.file_name)}
                  disabled={deleteLoading === job.job_uuid}
                >
                  {deleteLoading === job.job_uuid ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <DeleteIcon />
                  )}
                </Button>
              </Box>
              
              <Stack direction="column" spacing={1.5} sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                  <CalendarTodayIcon fontSize="small" sx={{ mr: 1, opacity: 0.7, minWidth: 20 }} />
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    Submitted: {formatDate(job.start_time)}
                  </Typography>
                </Box>
                
                {job.completion_time && job.status === 'complete' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                    <DateRangeIcon fontSize="small" sx={{ mr: 1, opacity: 0.7, minWidth: 20 }} />
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      Completed: {formatDate(job.completion_time)}
                    </Typography>
                  </Box>
                )}
                
                {job.submitted_by && (
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                    <PersonIcon fontSize="small" sx={{ mr: 1, opacity: 0.7, minWidth: 20 }} />
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      By: {job.submitted_by}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  };

  // Table view for desktop
  const renderJobTable = (status) => {
    const jobList = jobs[status] || [];
    
    if (jobList.length === 0) {
      return null;
    }
    
    return jobList.map((job, index) => (
      <TableRow 
        key={index} 
        sx={{ 
          '&:nth-of-type(odd)': { bgcolor: 'rgba(0, 0, 0, 0.02)' },
          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
          transition: 'background-color 0.2s',
          borderLeft: `4px solid ${getStatusColor(job.status)}`
        }}
      >
        <TableCell sx={{ py: 2 }}>
          {formatFileName(job.file_name, job)}
        </TableCell>
        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
          <Tooltip title={job.start_time ? new Date(job.start_time).toLocaleString() : ''}>
            <Box component="span">{formatDate(job.start_time)}</Box>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
          <Tooltip title={job.completion_time ? new Date(job.completion_time).toLocaleString() : ''}>
            <Box component="span">{formatDate(job.completion_time)}</Box>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
          {job.submitted_by || '-'}
        </TableCell>
        <TableCell align="right">
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            {job.status === 'complete' && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={downloadLoading === job.file_name ? null : <DownloadIcon />}
                onClick={() => handleDownload(job.file_name)}
                disabled={downloadLoading === job.file_name}
                sx={{ 
                  '&:hover': {
                    boxShadow: 2,
                  }
                }}
              >
                {downloadLoading === job.file_name ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Download'
                )}
              </Button>
            )}
            
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={deleteLoading === job.job_uuid ? null : <DeleteIcon />}
              onClick={() => handleShowDeleteConfirm(job.job_uuid, job.file_name)}
              disabled={deleteLoading === job.job_uuid}
            >
              {deleteLoading === job.job_uuid ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Delete'
              )}
            </Button>
          </Box>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 1, sm: 2, md: 4 } }}>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={3}
        flexDirection={isMobile ? "column" : "row"}
        gap={isMobile ? 2 : 0}
      >
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          component="h1" 
          fontWeight="medium"
          align={isMobile ? "center" : "left"}
          sx={{ width: isMobile ? '100%' : 'auto' }}
        >
          Jobs
        </Typography>
        <Tooltip title="Refresh job status">
          <IconButton 
            onClick={fetchJobs} 
            disabled={loading}
            color="primary"
            sx={{ 
              bgcolor: 'rgba(25, 118, 210, 0.1)',
              '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.2)' }
            }}
          >
            <RefreshIcon sx={{ animation: refreshing ? 'spin 1s linear' : 'none' }} />
          </IconButton>
        </Tooltip>
      </Box>
      
      {/* Error message */}
      {error && (
        <Fade in={!!error}>
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              boxShadow: 1,
              '& .MuiAlert-icon': { alignItems: 'center' }
            }}
            onClose={() => setError(null)}
          >
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
        </Fade>
      )}
      
      {/* Search and filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }} elevation={1}>
        <Grid container spacing={2} alignItems="center">
          {/* Search */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by file name"
              value={searchTerm}
              onChange={handleSearchChange}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClearSearch}
                      edge="end"
                      size="small"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          
          {/* Date filters */}
          <Grid item xs={6} md={2}>
            <TextField
              label="Start Date"
              type="date"
              fullWidth
              size="small"
              value={startDate && startDate instanceof Date && !isNaN(startDate) ? startDate.toISOString().split('T')[0] : ''}
              onChange={(e) => {
                if (e.target.value) {
                  handleStartDateChange(new Date(e.target.value));
                } else {
                  handleStartDateChange(null);
                }
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          
          <Grid item xs={6} md={2}>
            <TextField
              label="End Date"
              type="date"
              fullWidth
              size="small"
              value={endDate && endDate instanceof Date && !isNaN(endDate) ? endDate.toISOString().split('T')[0] : ''}
              onChange={(e) => {
                if (e.target.value) {
                  handleEndDateChange(new Date(e.target.value));
                } else {
                  handleEndDateChange(null);
                }
              }}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: startDate && startDate instanceof Date && !isNaN(startDate) ? startDate.toISOString().split('T')[0] : '',
              }}
            />
          </Grid>
          
          {/* Status filter */}
          <Grid item xs={12} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={selectedStatus}
                onChange={handleStatusChange}
                label="Status"
              >
                <MenuItem value="all">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography>All Statuses</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="complete">
                  <Box display="flex" alignItems="center" gap={1}>
                    <CheckCircleOutlineIcon sx={{ color: '#4caf50', fontSize: 18 }} />
                    <Typography>Completed</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="in_progress">
                  <Box display="flex" alignItems="center" gap={1}>
                    <HourglassEmptyIcon sx={{ color: '#ff9800', fontSize: 18 }} />
                    <Typography>In Progress</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="failed">
                  <Box display="flex" alignItems="center" gap={1}>
                    <ErrorOutlineIcon sx={{ color: '#f44336', fontSize: 18 }} />
                    <Typography>Failed</Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Clear filters button */}
          <Grid item xs={12} md={1}>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleClearFilters}
              disabled={!endDate && selectedStatus === 'complete' && !searchTerm}
              fullWidth
            >
              Clear
            </Button>
          </Grid>
          
          {/* Filter indicators for mobile */}
          {isMobile && (endDate || selectedStatus !== 'complete' || searchTerm) && (
            <Grid item xs={12}>
              <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                {startDate && (
                  <Chip 
                    label={`From: ${startDate.toLocaleDateString()}`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                )}
                
                {endDate && (
                  <Chip 
                    label={`To: ${endDate.toLocaleDateString()}`}
                    onDelete={() => setEndDate(null)}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                )}
                
                {selectedStatus !== 'complete' && (
                  <Chip 
                    key={selectedStatus}
                    label={`Status: ${selectedStatus === 'all' ? 'All' : 
                             selectedStatus === 'in_progress' ? 'In Progress' : 
                             selectedStatus === 'failed' ? 'Failed' : 'Completed'}`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                )}
                
                {searchTerm && (
                  <Chip 
                    label={`Search: ${searchTerm}`}
                    onDelete={handleClearSearch}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>
      
      {isMobile ? (
        /* Mobile Card View */
        renderJobCards('all')
      ) : (
        /* Desktop Table View */
        <TableContainer 
          component={Paper} 
          sx={{ 
            borderRadius: 2, 
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.08)'
          }}
          elevation={1}
        >
          <Table sx={{ minWidth: 650 }}>
            <TableHead sx={{ bgcolor: '#f8f9fa' }}>
              <TableRow>
                <TableCell 
                  sx={{ fontWeight: 'bold', fontSize: '1rem', py: 2 }}
                >
                  <TableSortLabel
                    active={sortConfig.sortBy === 'file_name'}
                    direction={sortConfig.sortBy === 'file_name' ? sortConfig.sortOrder : 'asc'}
                    onClick={() => handleSort('file_name')}
                  >
                    File Name
                  </TableSortLabel>
                </TableCell>
                <TableCell 
                  sx={{ fontWeight: 'bold', fontSize: '1rem', py: 2, display: { xs: 'none', md: 'table-cell' } }}
                >
                  <TableSortLabel
                    active={sortConfig.sortBy === 'request_start_time'}
                    direction={sortConfig.sortBy === 'request_start_time' ? sortConfig.sortOrder : 'asc'}
                    onClick={() => handleSort('request_start_time')}
                  >
                    Submission Date
                  </TableSortLabel>
                </TableCell>
                <TableCell 
                  sx={{ fontWeight: 'bold', fontSize: '1rem', py: 2, display: { xs: 'none', md: 'table-cell' } }}
                >
                  <TableSortLabel
                    active={sortConfig.sortBy === 'request_completion_time'}
                    direction={sortConfig.sortBy === 'request_completion_time' ? sortConfig.sortOrder : 'asc'}
                    onClick={() => handleSort('request_completion_time')}
                  >
                    Completion Date
                  </TableSortLabel>
                </TableCell>
                <TableCell 
                  sx={{ fontWeight: 'bold', fontSize: '1rem', py: 2, display: { xs: 'none', sm: 'table-cell' } }}
                >
                  <TableSortLabel
                    active={sortConfig.sortBy === 'user_id'}
                    direction={sortConfig.sortBy === 'user_id' ? sortConfig.sortOrder : 'asc'}
                    onClick={() => handleSort('user_id')}
                  >
                    Submitted By
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem', py: 2 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (!jobs.complete || jobs.complete.length === 0) && 
                (!jobs.in_progress || jobs.in_progress.length === 0) &&
                (!jobs.failed || jobs.failed.length === 0) ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                      <CircularProgress size={40} />
                      <Typography color="text.secondary">Loading jobs...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {selectedStatus === 'all' ? (
                    // For 'all' status, combine and sort all jobs
                    (() => {
                      // Combine all job arrays
                      let allJobs = [
                        ...(jobs.complete || []),
                        ...(jobs.in_progress || []),
                        ...(jobs.failed || [])
                      ];
                      
                      // Re-sort the combined list based on current sort config
                      allJobs.sort((a, b) => {
                        const aValue = a[sortConfig.sortBy === 'request_start_time' ? 'start_time' : 
                                        sortConfig.sortBy === 'request_completion_time' ? 'completion_time' : 
                                        sortConfig.sortBy];
                        const bValue = b[sortConfig.sortBy === 'request_start_time' ? 'start_time' : 
                                        sortConfig.sortBy === 'request_completion_time' ? 'completion_time' : 
                                        sortConfig.sortBy];
                        
                        if (!aValue && !bValue) return 0;
                        if (!aValue) return 1;
                        if (!bValue) return -1;
                        
                        let comparison;
                        if (sortConfig.sortBy.includes('time')) {
                          // Safely handle date comparison
                          try {
                            const dateA = new Date(aValue);
                            const dateB = new Date(bValue);
                            // Check if dates are valid before comparing
                            comparison = (dateA.toString() !== 'Invalid Date' && dateB.toString() !== 'Invalid Date') 
                              ? dateA - dateB
                              : String(aValue).localeCompare(String(bValue));
                          } catch (e) {
                            // Fallback to string comparison if date parsing fails
                            comparison = String(aValue).localeCompare(String(bValue));
                          }
                        } else {
                          comparison = String(aValue).localeCompare(String(bValue));
                        }
                          
                        return sortConfig.sortOrder === 'asc' ? comparison : -comparison;
                      });
                      
                      // Render all sorted jobs
                      return allJobs.map((job, index) => (
                        <TableRow 
                          key={index} 
                          sx={{ 
                            '&:nth-of-type(odd)': { bgcolor: 'rgba(0, 0, 0, 0.02)' },
                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                            transition: 'background-color 0.2s',
                            borderLeft: `4px solid ${getStatusColor(job.status)}`
                          }}
                        >
                          <TableCell sx={{ py: 2 }}>
                            {formatFileName(job.file_name, job)}
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <Tooltip title={job.start_time ? new Date(job.start_time).toLocaleString() : ''}>
                              <Box component="span">{formatDate(job.start_time)}</Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <Tooltip title={job.completion_time ? new Date(job.completion_time).toLocaleString() : ''}>
                              <Box component="span">{formatDate(job.completion_time)}</Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            {job.submitted_by || '-'}
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              {job.status === 'complete' && (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  startIcon={downloadLoading === job.file_name ? null : <DownloadIcon />}
                                  onClick={() => handleDownload(job.file_name)}
                                  disabled={downloadLoading === job.file_name}
                                  sx={{ 
                                    '&:hover': {
                                      boxShadow: 2,
                                    }
                                  }}
                                >
                                  {downloadLoading === job.file_name ? (
                                    <CircularProgress size={20} color="inherit" />
                                  ) : (
                                    'Download'
                                  )}
                                </Button>
                              )}
                              
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={deleteLoading === job.job_uuid ? null : <DeleteIcon />}
                                onClick={() => handleShowDeleteConfirm(job.job_uuid, job.file_name)}
                                disabled={deleteLoading === job.job_uuid}
                              >
                                {deleteLoading === job.job_uuid ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : (
                                  'Delete'
                                )}
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ));
                    })()
                  ) : (
                    // For specific status, render jobs for that status
                    <>
                      {/* Complete jobs */}
                      {jobs.complete && jobs.complete.length > 0 && 
                        renderJobTable('complete')}
                      
                      {/* In progress jobs */}
                      {jobs.in_progress && jobs.in_progress.length > 0 && 
                        renderJobTable('in_progress')}
                      
                      {/* Failed jobs */}
                      {jobs.failed && jobs.failed.length > 0 && 
                        renderJobTable('failed')}
                    </>
                  )}
                  
                  {/* No results message */}
                  {(!jobs.complete || jobs.complete.length === 0) && 
                   (!jobs.in_progress || jobs.in_progress.length === 0) &&
                   (!jobs.failed || jobs.failed.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                          <Typography color="text.secondary">No jobs found matching the current filters</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[20, 50, 100, 250]}
            component="div"
            count={pagination.totalCount || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            showFirstButton
            showLastButton
            sx={{
              borderTop: '1px solid rgba(0,0,0,0.08)',
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                my: 1
              }
            }}
          />
        </TableContainer>
      )}

      {/* Loading indicator for mobile */}
      {isMobile && loading && (!jobs.complete || jobs.complete.length === 0) && 
        (!jobs.in_progress || jobs.in_progress.length === 0) &&
        (!jobs.failed || jobs.failed.length === 0) && (
        <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={2}>
          <CircularProgress size={40} />
          <Typography color="text.secondary">Loading jobs...</Typography>
        </Box>
      )}

      <Box 
        display="flex" 
        justifyContent="center" 
        mt={4} 
        mb={3}
        sx={{ opacity: 0.7 }}
      >
        <Typography variant="caption" color="text.secondary">
          Last updated: {new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
        </Typography>
      </Box>

      {/* Delete confirmation dialog */}
      <Dialog
        open={confirmDeleteDialog.open}
        onClose={handleCloseDeleteConfirm}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the summary for "{confirmDeleteDialog.fileName}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseDeleteConfirm} 
            color="primary"
            disabled={deleteLoading === confirmDeleteDialog.jobUuid}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={deleteLoading === confirmDeleteDialog.jobUuid}
            startIcon={deleteLoading === confirmDeleteDialog.jobUuid ? 
              <CircularProgress size={20} color="inherit" /> : 
              <DeleteIcon />
            }
          >
            {deleteLoading === confirmDeleteDialog.jobUuid ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default Jobs;